import { useState, useEffect, useCallback, useRef } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import { DataGrid, GridToolbarContainer } from '@mui/x-data-grid'
import RequestQuoteIcon from '@mui/icons-material/RequestQuote'
import { usePdfReceipt } from '../PdfDocument/usePdfReceipt'


// ---------------------------------------------------------------------------
// Pivot helper
// ---------------------------------------------------------------------------
function pivotTransactions(data) {
    const grouped = {}
    const categorySet = new Set()

    ;(data ?? []).forEach((t) => {
        const bucket = t.transactions_bucket
        if (!bucket) return

        const leaseId = bucket.lease_id
        const key = `${leaseId}@${t.period_from}`
        categorySet.add(bucket.category)

        if (!grouped[key]) {
            grouped[key] = {
                id:          key,
                short_name:  bucket.lease?.property?.short_name ?? '',
                period_from: t.period_from,
                on:          t.on,
            }
        }

        grouped[key][bucket.category] =
            (grouped[key][bucket.category] ?? 0) + (t.value ?? 0)
    })

    const rows = Object.values(grouped).sort((a, b) =>
        b.period_from.localeCompare(a.period_from)
    )

    return { rows, categoryColumns: [...categorySet].sort() }
}


// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------
const COLUMN_LABEL_OVERRIDES = {
    short_name:  'Bail',
    period_from: 'Début période',
    on:          'Payé le',
}

/**
 * DECISION: when scoped to a single property (hidePropertyName = true), omit
 * the 'Bail' / 'short_name' column.  All rows share the same property name,
 * so the column carries no information and wastes horizontal space.
 */
function buildColumns(categoryColumns, hidePropertyName = false) {
    const staticFields = hidePropertyName
        ? ['period_from', 'on']
        : ['short_name', 'period_from', 'on']

    const staticCols = staticFields.map((field) => ({
        field,
        headerName: COLUMN_LABEL_OVERRIDES[field],
        flex: 1,
    }))

    const dynamicCols = categoryColumns.map((cat) => ({
        field:      cat,
        headerName: cat,
        flex:       1,
        type:       'number',
    }))

    return [...staticCols, ...dynamicCols]
}


// ---------------------------------------------------------------------------
// Component
//
// Supports two usage modes:
//
//   Property-page mode  (propertyId provided)
//     - Fetches only transactions for that property.
//     - Omits the 'Bail' column (redundant when scoped to one property).
//     - Manages row-selection state internally — the receipt button in the
//       toolbar still works without any external wiring.
//
//   Portfolio-wide mode  (no propertyId — used on the Payments page)
//     - Fetches all transactions across the portfolio.
//     - Exposes row selection via optional controlled props so the parent can
//       read the selected lease/period for actions outside this component.
//     - Falls back to internal state when the controlled props are absent.
// ---------------------------------------------------------------------------
export default function PaymentsTable({
                                          supabase,
                                          propertyId = null,

                                          // Controlled selection — all four must be provided together, or none.
                                          // When omitted, the component manages selection internally.
                                          selectedLeaseId:       extLeaseId,
                                          setSelectedLeaseId:    setExtLeaseId,
                                          selectedPeriodFrom:    extPeriodFrom,
                                          setSelectedPeriodFrom: setExtPeriodFrom,
                                      }) {

    // ── Controlled / uncontrolled selection ───────────────────────────────────
    const [intLeaseId,    setIntLeaseId]    = useState(null)
    const [intPeriodFrom, setIntPeriodFrom] = useState(null)

    const isControlled          = extLeaseId !== undefined
    const selectedLeaseId       = isControlled ? extLeaseId       : intLeaseId
    const setSelectedLeaseId    = isControlled ? setExtLeaseId    : setIntLeaseId
    const selectedPeriodFrom    = isControlled ? extPeriodFrom    : intPeriodFrom
    const setSelectedPeriodFrom = isControlled ? setExtPeriodFrom : setIntPeriodFrom

    const [rows,    setRows]    = useState([])
    const [columns, setColumns] = useState(buildColumns([], !!propertyId))
    const [loading, setLoading] = useState(true)
    const [error,   setError]   = useState(null)

    const { generateReceipt, receiptLoading, receiptError, clearReceiptError } =
        usePdfReceipt(supabase)


    // =========================================================================
    // Fetch and pivot
    // =========================================================================
    const fetchPayments = useCallback(async () => {
        setError(null)

        try {
            // Build the base transaction query.  The select is identical in
            // both modes — we always join to property so pivotTransactions can
            // populate short_name, even though we hide that column on the
            // property page.
            let transactionQuery = supabase
                .from('transaction')
                .select(`
                    id,
                    period_from,
                    on,
                    value,
                    transactions_bucket (
                        id,
                        category,
                        lease_id,
                        lease:lease_id (
                            id,
                            property:property_id (
                                short_name
                            )
                        )
                    )
                `)

            if (propertyId) {
                // DECISION: resolve bucket IDs in two explicit steps rather than
                // relying on a deep nested PostgREST filter across three joins
                // (transaction → transactions_bucket → lease → property), which
                // is not reliably supported in supabase-js v2.  Two lightweight
                // queries are clearer and more dependable.

                // Step A — all lease IDs for this property (active + historical,
                // so the full payment history is shown, not just the active lease)
                const { data: leases, error: leaseErr } = await supabase
                    .from('lease')
                    .select('id')
                    .eq('property_id', propertyId)
                if (leaseErr) throw leaseErr

                const leaseIds = (leases ?? []).map(l => l.id)
                if (!leaseIds.length) {
                    setRows([])
                    setColumns(buildColumns([], true))
                    return
                }

                // Step B — all bucket IDs across those leases (active + expired,
                // so payments recorded before a re-indexation are also included)
                const { data: buckets, error: bucketErr } = await supabase
                    .from('transactions_bucket')
                    .select('id')
                    .in('lease_id', leaseIds)
                if (bucketErr) throw bucketErr

                const bucketIds = (buckets ?? []).map(b => b.id)
                if (!bucketIds.length) {
                    setRows([])
                    setColumns(buildColumns([], true))
                    return
                }

                transactionQuery = transactionQuery.in('bucket_id', bucketIds)
            }

            const { data, error: fetchError } = await transactionQuery
            if (fetchError) throw fetchError

            const { rows: pivotedRows, categoryColumns } = pivotTransactions(data)
            setRows(pivotedRows)
            setColumns(buildColumns(categoryColumns, !!propertyId))
        } catch (err) {
            setError(err.message ?? 'Erreur lors du chargement des paiements.')
        } finally {
            // finally always executes, including after an early return inside try
            setLoading(false)
        }
    }, [supabase, propertyId])


    // =========================================================================
    // Initial fetch + debounced realtime subscription
    // =========================================================================
    const realtimeTimerRef = useRef(null)
    const debouncedFetch = useCallback(() => {
        if (realtimeTimerRef.current) clearTimeout(realtimeTimerRef.current)
        realtimeTimerRef.current = setTimeout(fetchPayments, 400)
    }, [fetchPayments])

    useEffect(() => {
        fetchPayments()

        // Unique channel name per instance so a property-page PaymentsTable
        // and the portfolio-wide one can coexist without collisions.
        const channelName = propertyId
            ? `payments-table-realtime-${propertyId}`
            : 'payments-table-realtime'

        const channel = supabase
            .channel(channelName)
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'transaction' },
                debouncedFetch
            )
            .subscribe()

        return () => {
            if (realtimeTimerRef.current) clearTimeout(realtimeTimerRef.current)
            supabase.removeChannel(channel)
        }
    }, [fetchPayments, debouncedFetch, supabase, propertyId])


    // =========================================================================
    // Toolbar
    // =========================================================================
    const CustomToolbar = () => (
        <GridToolbarContainer>
            <Button
                variant="text"
                size="small"
                startIcon={
                    receiptLoading
                        ? <CircularProgress size={16} color="inherit" />
                        : <RequestQuoteIcon />
                }
                onClick={() => generateReceipt(selectedLeaseId, selectedPeriodFrom)}
                disabled={!selectedLeaseId || !selectedPeriodFrom || receiptLoading}
            >
                {receiptLoading ? 'Génération…' : 'Quittance'}
            </Button>
        </GridToolbarContainer>
    )


    // =========================================================================
    // Render
    // =========================================================================
    return (
        <Box sx={{ width: '100%' }}>
            {/* Table-level fetch error — replaces the grid */}
            {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Receipt error — shown above the grid; grid stays usable */}
            {receiptError && (
                <Alert severity="error" onClose={clearReceiptError} sx={{ mb: 1 }}>
                    {receiptError}
                </Alert>
            )}

            {!error && (
                <DataGrid
                    autoHeight
                    loading={loading}
                    checkboxSelection
                    disableMultipleRowSelection
                    rows={rows}
                    columns={columns}
                    rowSelectionModel={
                        selectedLeaseId && selectedPeriodFrom
                            ? [`${selectedLeaseId}@${selectedPeriodFrom}`]
                            : []
                    }
                    onRowSelectionModelChange={(model) => {
                        const id = model.length ? model[0] : null
                        setSelectedLeaseId(id ? id.split('@')[0] : null)
                        setSelectedPeriodFrom(id ? id.split('@')[1] : null)
                    }}
                    getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                    }
                    initialState={{
                        pagination: { paginationModel: { pageSize: 20 } },
                    }}
                    pageSizeOptions={[10, 20, 50]}
                    density="compact"
                    slots={{ toolbar: CustomToolbar }}
                />
            )}
        </Box>
    )
}